import logger from '../logger';
import localforage from 'localforage';
import {
  EXCEL_CHECK_EXISTING_SESSION,
  EXCEL_UPDATE_SESSION,
  EXCEL_LIVENESS_PROBE,
  EXCEL_CLEAR_SESSION,
  EXCEL_SESSION_SUBSCRIPTION,
} from './queries';
import type { SessionData, SessionUpdateResponse, SessionSubscriptionData } from './types';

const LOCAL_SESSIONS_KEY = 'artis_local_excel_sessions';
const PERSISTED_SESSION_ID_KEY = 'artis_excel_session_id';
const SESSION_BROADCAST_KEY = 'artis_excel_session_broadcast';

export type SessionBroadcastMessage = 
  | { type: 'session_started'; sessionId: string; userId: string }
  | { type: 'session_ended'; sessionId: string; userId: string }
  | { type: 'user_logged_in'; userId: string }
  | { type: 'user_logged_out' }
  | { type: 'token_received'; token: string };

/**
 * Broadcast a session event to other add-ins on the same machine
 * Uses localStorage which fires storage events across different contexts
 */
export function broadcastSessionEvent(message: SessionBroadcastMessage): void {
  try {
    // Add timestamp to ensure the value changes (storage event only fires on change)
    const payload = JSON.stringify({ ...message, _timestamp: Date.now() });
    localStorage.setItem(SESSION_BROADCAST_KEY, payload);
    logger.debug('Broadcasted session event via localStorage', { message });
  } catch (error) {
    logger.warn('Failed to broadcast session event', { error, message });
  }
}

/**
 * Subscribe to session events from other add-ins
 * Uses storage event which fires when localStorage changes in another context
 */
export function subscribeToSessionBroadcast(
  callback: (message: SessionBroadcastMessage) => void
): () => void {
  const handler = (event: StorageEvent) => {
    if (event.key !== SESSION_BROADCAST_KEY || !event.newValue) {
      return;
    }
    
    try {
      const payload = JSON.parse(event.newValue) as SessionBroadcastMessage & { _timestamp: number };
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { _timestamp, ...message } = payload;
      logger.debug('Received session broadcast via storage event', { message });
      callback(message as SessionBroadcastMessage);
    } catch (error) {
      logger.warn('Failed to parse session broadcast', { error, newValue: event.newValue });
    }
  };
  
  window.addEventListener('storage', handler);
  
  return () => {
    window.removeEventListener('storage', handler);
  };
}

/**
 * Get all locally registered session IDs (sessions running on this machine)
 */
export async function getLocalSessions(): Promise<string[]> {
  const sessions = await localforage.getItem<string[]>(LOCAL_SESSIONS_KEY);
  return sessions || [];
}

/**
 * Register a session ID as running locally on this machine
 */
export async function registerLocalSession(sessionId: string): Promise<void> {
  const sessions = await getLocalSessions();
  if (!sessions.includes(sessionId)) {
    sessions.push(sessionId);
    await localforage.setItem(LOCAL_SESSIONS_KEY, sessions);
    logger.debug('Registered local session', { sessionId, totalLocalSessions: sessions.length });
  }
}

/**
 * Unregister a session ID from local sessions
 */
export async function unregisterLocalSession(sessionId: string): Promise<void> {
  const sessions = await getLocalSessions();
  const filtered = sessions.filter(id => id !== sessionId);
  await localforage.setItem(LOCAL_SESSIONS_KEY, filtered);
  logger.debug('Unregistered local session', { sessionId, remainingLocalSessions: filtered.length });
}

/**
 * Check if a session ID is registered as running locally
 */
export async function isLocalSession(sessionId: string): Promise<boolean> {
  const sessions = await getLocalSessions();
  return sessions.includes(sessionId);
}

/**
 * Get or create a persisted session ID for this user
 * This allows the session ID to survive page reloads
 */
export async function getOrCreatePersistedSessionId(userId: string): Promise<string> {
  const key = `${PERSISTED_SESSION_ID_KEY}_${userId}`;
  const existingId = await localforage.getItem<string>(key);
  
  if (existingId) {
    logger.debug('Reusing persisted session ID', { sessionId: existingId });
    return existingId;
  }
  
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);
  const newId = `excel_${userId}_${timestamp}_${random}`;
  
  await localforage.setItem(key, newId);
  logger.debug('Created and persisted new session ID', { sessionId: newId });
  return newId;
}

/**
 * Clear the persisted session ID (on logout)
 */
export async function clearPersistedSessionId(userId: string): Promise<void> {
  const key = `${PERSISTED_SESSION_ID_KEY}_${userId}`;
  await localforage.removeItem(key);
  logger.debug('Cleared persisted session ID', { userId });
}

interface WebSocketMessage {
  type: string;
  id?: string;
  payload?: {
    data?: unknown;
    errors?: unknown[];
  };
}

export class ExcelSessionClient {
  private hasuraUrl: string;
  private wsUrl: string;
  private token: string;
  private livenessInterval: number | null = null;
  private subscriptionClient: WebSocket | null = null;
  public excelSessionId: string;

  constructor(hasuraDbName: string, token: string, excelSessionId: string) {
    const baseUrl = `https://${hasuraDbName}.hasura.app`;
    this.hasuraUrl = `${baseUrl}/v1/graphql`;
    this.wsUrl = `wss://${hasuraDbName}.hasura.app/v1/graphql`;
    this.token = token;
    this.excelSessionId = excelSessionId;
    
    logger.debug('ExcelSessionClient initialized', { 
      hasuraDbName, 
      hasuraUrl: this.hasuraUrl,
      excelSessionId: this.excelSessionId
    });
  }

  private async executeMutation<T>(
    query: string,
    variables: Record<string, unknown>
  ): Promise<T> {
    try {
      logger.debug('Executing mutation', { query: query.split('\n')[1], variables });
      
      const response = await fetch(this.hasuraUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`,
        },
        body: JSON.stringify({
          query,
          variables,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json() as { data?: T; errors?: unknown[] };

      if (result.errors) {
        logger.error('GraphQL mutation errors', { errors: result.errors });
        throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
      }

      logger.debug('Mutation executed successfully', { data: result.data });
      return result.data as T;
    } catch (error) {
      logger.error('Failed to execute mutation', { error, query, variables });
      throw error;
    }
  }

  async updateSession(sessionData: SessionData): Promise<SessionUpdateResponse> {
    const result = await this.executeMutation<{ insert_session_one: SessionUpdateResponse }>(
      EXCEL_UPDATE_SESSION,
      {
        user: sessionData.user_id,
        excel_session_id: sessionData.excel_session_id,
      }
    );

    if (!result.insert_session_one) {
      throw new Error('Session update/insert failed - check Hasura permissions');
    }

    logger.info('Excel session updated/created', {
      excel_session_id: result.insert_session_one.excel_session_id,
      lastSeen: result.insert_session_one.last_seen,
    });

    return result.insert_session_one;
  }

  async checkExistingSession(userId: string): Promise<{
    id: string;
    excel_session_id: string | null;
    last_seen: string;
  } | null> {
    try {
      const result = await this.executeMutation<{ 
        session_by_pk: {
          id: string;
          excel_session_id: string | null;
          last_seen: string;
        } | null;
      }>(EXCEL_CHECK_EXISTING_SESSION, {
        user: userId,
      });

      const existingSession = result?.session_by_pk;
      
      if (existingSession?.excel_session_id) {
        logger.info('Existing Excel session found', { 
          userId,
          existing_excel_session_id: existingSession.excel_session_id,
          my_excel_session_id: this.excelSessionId,
        });
        return existingSession;
      }

      logger.info('No existing Excel session found', { userId });
      return null;
    } catch (error) {
      logger.error('Failed to check existing session', { error, userId });
      throw error;
    }
  }

  async sendLivenessProbe(userId: string): Promise<void> {
    try {
      await this.executeMutation(EXCEL_LIVENESS_PROBE, {
        user: userId,
        excel_session_id: this.excelSessionId,
      });
      logger.debug('Liveness probe sent', { userId, excel_session_id: this.excelSessionId });
    } catch (error) {
      logger.error('Failed to send liveness probe', { error, userId });
      throw error;
    }
  }

  startLivenessProbe(userId: string): void {
    if (this.livenessInterval !== null) {
      logger.warn('Liveness probe already running');
      return;
    }

    logger.info('Starting liveness probe interval', { userId, intervalMs: 30000 });

    this.sendLivenessProbe(userId).catch((error) => {
      logger.error('Initial liveness probe failed', { error });
    });

    this.livenessInterval = window.setInterval(() => {
      this.sendLivenessProbe(userId).catch((error) => {
        logger.error('Periodic liveness probe failed', { error });
      });
    }, 30000);
  }

  stopLivenessProbe(): void {
    if (this.livenessInterval !== null) {
      logger.info('Stopping liveness probe interval');
      window.clearInterval(this.livenessInterval);
      this.livenessInterval = null;
    }
  }

  async clearSession(userId: string): Promise<void> {
    try {
      const result = await this.executeMutation<{ 
        update_session: { 
          affected_rows: number;
          returning: Array<{ id: string; excel_session_id: string | null }>;
        } 
      }>(EXCEL_CLEAR_SESSION, {
        user: userId,
        excel_session_id: this.excelSessionId,
      });
      
      const affectedRows = result?.update_session?.affected_rows ?? 0;
      
      if (affectedRows > 0) {
        logger.info('Excel session cleared', { userId, excel_session_id: this.excelSessionId });
      } else {
        logger.info('Excel session already cleared or taken over', { userId, excel_session_id: this.excelSessionId });
      }
    } catch (error) {
      logger.error('Failed to clear session', { error, userId });
      throw error;
    }
  }


  subscribeToSession(
    userId: string,
    onSessionChange: (data: SessionSubscriptionData) => void,
    onError?: (error: Error) => void
  ): () => void {
    try {
      logger.info('Subscribing to Excel session changes', { userId });

      this.subscriptionClient = new WebSocket(this.wsUrl, 'graphql-ws');

      this.subscriptionClient.onopen = () => {
        logger.debug('WebSocket connection opened');
        this.subscriptionClient?.send(
          JSON.stringify({
            type: 'connection_init',
            payload: {
              headers: {
                Authorization: `Bearer ${this.token}`,
              },
            },
          })
        );
      };

      this.subscriptionClient.onmessage = (event) => {
        const message = JSON.parse(event.data) as WebSocketMessage;
        logger.debug('WebSocket message received', { type: message.type });

        switch (message.type) {
          case 'connection_ack':
            logger.debug('WebSocket connection acknowledged');
            this.subscriptionClient?.send(
              JSON.stringify({
                id: '1',
                type: 'start',
                payload: {
                  query: EXCEL_SESSION_SUBSCRIPTION,
                  variables: { user: userId },
                },
              })
            );
            break;

          case 'data':
            if (message.payload?.data) {
              onSessionChange(message.payload.data as SessionSubscriptionData);
            }
            break;

          case 'error':
            logger.error('WebSocket subscription error', { error: message.payload });
            if (onError) {
              onError(new Error(JSON.stringify(message.payload)));
            }
            break;

          case 'complete':
            logger.debug('WebSocket subscription completed');
            break;
        }
      };

      this.subscriptionClient.onerror = (error) => {
        logger.error('WebSocket error', { error });
        if (onError) {
          onError(new Error('WebSocket connection error'));
        }
      };

      this.subscriptionClient.onclose = () => {
        logger.debug('WebSocket connection closed');
      };

      return () => {
        this.unsubscribeFromSession();
      };
    } catch (error) {
      logger.error('Failed to subscribe to session', { error, userId });
      if (onError) {
        onError(error as Error);
      }
      return () => {};
    }
  }

  unsubscribeFromSession(): void {
    if (this.subscriptionClient) {
      logger.info('Unsubscribing from session changes');
      this.subscriptionClient.send(
        JSON.stringify({
          id: '1',
          type: 'stop',
        })
      );
      this.subscriptionClient.close();
      this.subscriptionClient = null;
    }
  }

  cleanup(): void {
    logger.info('Cleaning up ExcelSessionClient');
    this.stopLivenessProbe();
    this.unsubscribeFromSession();
  }
}

